'use client';

/**
 * @fileoverview Inline transparency indicator for a single tool
 * invocation made by the assistant during a turn. Renders as a small
 * pill with a spinner (in-flight), check (done), or warning (error).
 */

import { Loader2, Check, AlertTriangle } from 'lucide-react';
import type { ChatToolCall } from '@/domains/chat/schemas';
import { cn } from '@/lib/utils';

const TOOL_LABELS: Record<string, (input: unknown) => string> = {
  readChapter: (input) => {
    const n = (input as { number?: number })?.number;
    return typeof n === 'number' ? `Reading Chapter ${n}` : 'Reading chapter';
  },
  readCharacter: (input) => {
    const name = (input as { name?: string })?.name;
    return name ? `Reading ${name}'s profile` : 'Reading character';
  },
  readWorldElement: (input) => {
    const name = (input as { name?: string })?.name;
    return name ? `Reading ${name}` : 'Reading world element';
  },
  readPlotThreads: () => 'Reviewing plot threads',
  readStyleGuide: () => 'Reviewing style guide',
  readStoryStructure: () => 'Reviewing story structure',
};

function labelFor(toolCall: ChatToolCall): string {
  const fn = TOOL_LABELS[toolCall.name];
  return fn ? fn(toolCall.input) : `Calling ${toolCall.name}`;
}

export function ToolCallPill({ toolCall }: { toolCall: ChatToolCall }) {
  const isDone = Boolean(toolCall.completedAt);
  const hasError = Boolean(toolCall.error);

  const Icon = hasError ? AlertTriangle : isDone ? Check : Loader2;

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs',
        hasError
          ? 'border-destructive/40 bg-destructive/10 text-destructive'
          : isDone
            ? 'border-border bg-muted text-muted-foreground'
            : 'border-border bg-muted/60 text-muted-foreground',
      )}
    >
      <Icon
        className={cn(
          'h-3 w-3',
          !isDone && !hasError && 'animate-spin',
        )}
      />
      <span>{labelFor(toolCall)}</span>
      {isDone && !hasError && <span className="sr-only">(done)</span>}
    </div>
  );
}
